'use client';

/* eslint-disable max-lines */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { IS_DESKTOP_LOGOUT_ON_EXIT_ENABLED } from '@/features/flags/legacy-flags';
import {
  clearPersistedModels,
  logout,
  logoutFromServer,
  setAuthProviderAsSessionCookie,
  setToken,
} from '@/lib/authActions';
import { AuthProvider } from '@/models/authModel';
import { useDesktopAuthStore } from '@/models/desktopAuthSlice';
import { store } from '@/store';
import { recorderFmc } from '@/tauri/controller/recording';
import { getPersistedAuthProvider } from '@/utils/auth/persistedAuthProvider';
import { isDesktopOfflineNetworkMode } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import {
  appendCheckpoint,
  cancelTracking,
  finishTracking,
  TrackingKey,
} from '@heidi/runtime/performance';
import { trackAuthSuccess } from './analytics';
import { DesktopAuthContext, type DesktopAuthContextValue } from './desktop-auth-context';
import { attributeDownloadOnSignup } from './downloadAttribution';
import { setupAuthEventListeners } from './events';
import {
  desktopAuthBridge,
  getDesktopAuthError,
  type DesktopAuthErrorEventPayload,
  type DesktopAuthLoginOptions,
  type DesktopAuthSession,
  type DesktopDownloadAttributionPayload,
} from './index';
import {
  isLoginPerfEntryOpen,
  launchDesktopAuthLogin,
  markLoginPerfEntryClosed,
} from './login';
import { getPersistedAuth0DesktopToken } from './persisted-token';

const defaultAuthSession: DesktopAuthSession = {
  isAuthenticated: false,
  hasRefreshToken: false,
};

function getOfflineAuth0DesktopSession(
  latestSession: DesktopAuthSession
): (DesktopAuthSession & { accessToken: string }) | null {
  if (latestSession.isAuthenticated || !latestSession.hasRefreshToken) return null;
  if (!isDesktopOfflineNetworkMode()) return null;
  if (getPersistedAuthProvider() !== AuthProvider.AUTH0_DESKTOP) return null;

  const persistedToken = getPersistedAuth0DesktopToken();
  if (!persistedToken) return null;

  return {
    ...latestSession,
    isAuthenticated: true,
    hasRefreshToken: true,
    accessToken: persistedToken,
  };
}

export function DesktopAuthProvider({
  children,
}: Readonly<PropsWithChildren>): JSX.Element {
  const [authSession, setAuthSession] = useState<DesktopAuthSession | null>(null);

  // In-flight guard for a `login()` call this provider started.
  //
  // The Rust plugin's deep-link callback delivers login failures
  // (user denied consent, Auth0 error, cancelled) via the
  // `auth:error` event handled by `handleAuthError` below —
  // `launchDesktopAuthLogin` itself resolves as soon as the
  // external browser opens and does NOT reject on these outcomes.
  // Without a rollback here, the early
  // `setAuthProviderAsSessionCookie(AUTH0_DESKTOP)` dispatch stays
  // persisted with no Auth0 session ever arriving; the
  // `useIsAuthenticated` Kinde fallback is `!isTauri()`-gated, so
  // the user presents as signed-out until they explicitly log out.
  //
  // The ref is populated in `login()` before the early dispatch
  // and cleared on any of the three settled outcomes
  // (`handleLoginSuccess`, `handleAuthError`, launch throw).
  // `handleAuthError` MUST check `ref.current !== null` before
  // rolling back — the `auth:error` event can fire for events
  // unrelated to a login this provider initiated, and an ungated
  // rollback would clobber a valid provider mid-session.
  const loginAttemptRef = useRef<{ priorProvider: AuthProvider | null } | null>(null);

  const loadAuthSession = useCallback(async () => {
    if (!desktopAuthBridge.isAvailable()) {
      setAuthSession(defaultAuthSession);
      return;
    }

    try {
      const latestSession = await desktopAuthBridge.getAuthSession();
      const offlineSession = getOfflineAuth0DesktopSession(latestSession);

      if (offlineSession) {
        logger.info(
          '[DesktopAuthProvider] Restored Auth0 desktop session from local token while offline'
        );
        // Dispatch the provider alongside the token so Redux converges
        // at the source of restore. Historically only the two LOGIN
        // paths (session-event handler + interactive `login()`) wrote
        // `state.auth.authProvider`; legacy installs whose persisted
        // slice carried a token but no `authProvider` were unaffected
        // while nothing routed on it, but the dispatcher's desktop
        // null-provider transient gate now makes that state a
        // permanent `'loading'` hang. Dispatching here (and in the
        // normal-restore path below) converges Redux + the persisted
        // provider store on every boot.
        setAuthProviderAsSessionCookie(AuthProvider.AUTH0_DESKTOP);
        setToken(offlineSession.accessToken);
        setAuthSession(offlineSession);
        return;
      }

      // If Rust side has no session and no refresh token, the user was
      // logged out (via logout-on-exit or crash recovery). Clear any
      // stale FE persisted state (localStorage, React Query cache, etc.)
      // Only clear if user was actually using Auth0 Desktop auth — for Kinde
      // users, the Auth0 store is always empty and we must NOT clear their tokens.
      if (!latestSession.isAuthenticated && !latestSession.hasRefreshToken) {
        const persistedProvider = getPersistedAuthProvider();
        if (persistedProvider === AuthProvider.AUTH0_DESKTOP) {
          await clearPersistedModels();
        }
      }

      // Normal Rust-session restore: the Rust plugin reports an
      // authenticated session (Auth0-desktop is the only provider that
      // writes to its store, so an authenticated Rust session
      // uniquely identifies an Auth0-desktop user). Dispatch the
      // provider so Redux converges at the source — see the
      // offline-restore path above for the null-provider-hang
      // rationale. Token dispatch is deferred to the token cache's
      // Redux mirror-write on the next successful `getAccessToken()`.
      if (latestSession.isAuthenticated) {
        setAuthProviderAsSessionCookie(AuthProvider.AUTH0_DESKTOP);
      }

      setAuthSession(latestSession);
    } catch (error) {
      logger.error('[DesktopAuthProvider] Failed to fetch session', error);
      setAuthSession(defaultAuthSession);
    }
  }, []);

  useEffect(() => {
    void loadAuthSession();
  }, [loadAuthSession]);

  useEffect(() => {
    if (!desktopAuthBridge.isAvailable() || typeof window === 'undefined') {
      return;
    }

    const handleOnline = () => {
      void loadAuthSession();
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [loadAuthSession]);

  // Sync logout-on-exit feature flag to Rust layer after user is authenticated
  // Feature flags are user-based, so we need to wait until the user is logged in
  // to get the correct flag value
  const syncLogoutOnExit = useCallback(async () => {
    if (!desktopAuthBridge.isAvailable()) {
      return;
    }

    try {
      const enabled = IS_DESKTOP_LOGOUT_ON_EXIT_ENABLED() ?? false;
      await desktopAuthBridge.setLogoutOnExit(enabled);
      logger.debug('[DesktopAuthProvider] Synced logout-on-exit flag:', enabled);
    } catch (error) {
      logger.warn('[DesktopAuthProvider] Failed to sync logout-on-exit flag', error);
    }
  }, []);

  // Sync when user becomes authenticated (either from fresh login or session restore)
  useEffect(() => {
    if (authSession?.isAuthenticated) {
      void syncLogoutOnExit();
    }
  }, [authSession?.isAuthenticated, syncLogoutOnExit]);

  useEffect(() => {
    if (!desktopAuthBridge.isAvailable()) {
      return;
    }

    let cleanup: (() => void) | null = null;
    let cancelled = false;

    const handleAuthError = (payload: DesktopAuthErrorEventPayload) => {
      // Gate the tracking cancel on entry ownership, not on provider-
      // initiated-ness: the migration modal and offline re-auth launch via
      // launchDesktopAuthLogin without setting loginAttemptRef, and their
      // failures land here too. handleLoginSuccess takes ownership (clearing
      // the flag) before its finish frame, so an unrelated auth:error in that
      // window cannot drop a successful login's report.
      if (isLoginPerfEntryOpen()) {
        cancelTracking({ key: TrackingKey.SCRIBE_LOGIN });
        markLoginPerfEntryClosed();
      }
      const desktopAuthStore = useDesktopAuthStore.getState();
      // A failed/cancelled Apple-migration login leaves the user on Kinde.
      // Clear the in-flight marker (idempotent) so a later normal Auth0 login
      // isn't mis-classified as a migration completion — which would trigger a
      // spurious Kinde revoke. (APP-6026)
      desktopAuthStore.clearMigrationInProgress();
      const content = getDesktopAuthError(payload.code);
      desktopAuthStore.setAuthError(content, {
        correlationId: payload.correlation_id ?? null,
        attemptId: payload.attempt_id ?? null,
        detail: payload.description ?? null,
      });

      // Provider rollback for a login this provider initiated. The
      // ref is only set inside `login()`; an ungated rollback here
      // would clobber a valid provider mid-session on `auth:error`
      // events unrelated to a login we started.
      const attempt = loginAttemptRef.current;
      if (attempt !== null) {
        loginAttemptRef.current = null;
        if (attempt.priorProvider !== null) {
          store.dispatch.auth._setAuthProvider(attempt.priorProvider);
        } else {
          store.dispatch.auth.clearAuthProvider();
        }
        logger.warn(
          '[DesktopAuthProvider] auth:error during in-flight login; provider restored',
          {
            code: payload.code,
            priorProvider: attempt.priorProvider,
          }
        );
      }
    };

    const handleLoginSuccess = async (newSession: DesktopAuthSession) => {
      logger.info('[DesktopAuthProvider] Auth0 login success, syncing token to Redux');
      appendCheckpoint({
        key: TrackingKey.SCRIBE_LOGIN,
        checkpointName: 'auth_callback_received',
      });
      // Take ownership of the entry: from here the refresh chain below
      // finishes (or cancels) it, and handleAuthError must no longer be able
      // to cancel it out from under a success in flight. Anything that throws
      // between here and the refresh chain would leave the entry unowned, so
      // the rest of the handler closes it on failure.
      markLoginPerfEntryClosed();
      try {
        // Login settled successfully — clear the in-flight rollback
        // guard so a later unrelated `auth:error` (e.g. a subsequent
        // failed login attempt) can't roll back this session's
        // provider.
        loginAttemptRef.current = null;

        const desktopAuthStore = useDesktopAuthStore.getState();
        // The attempt settled: release the sign-in buttons' pending guard
        // (the error path clears it inside setAuthError; the watchdog in
        // useDesktopLoginPending covers silent abandonment).
        desktopAuthStore.clearLoginPending();
        const isMigration = desktopAuthStore.migrationInProgress;

        // Kinde→Auth0 Apple migration completion (APP-6026): revoke the old Kinde
        // session BEFORE swapping the Redux token. `logoutFromServer` reads the
        // *current* Redux token — still the Kinde token here, because the
        // migration login was launched via `launchDesktopAuthLogin` without
        // flipping `authProvider` — and POSTs /user/logout (Kinde-issuer scoped),
        // so it revokes Kinde and never touches the freshly minted Auth0 session.
        // Mirrors the web ordering in `useKindeMigrationCompletion`. Best-effort:
        // the effect swallows its own errors, so a failed revoke must not strand
        // the already-migrated user — we await it, then proceed regardless.
        if (isMigration) {
          await logoutFromServer();
        }

        // Sync token directly from event payload - no need to call getAccessToken()
        setAuthProviderAsSessionCookie(AuthProvider.AUTH0_DESKTOP);
        if (newSession.accessToken) {
          setToken(newSession.accessToken);
        }
        appendCheckpoint({
          key: TrackingKey.SCRIBE_LOGIN,
          checkpointName: 'token_stored',
        });

        // Migration finished: drop the marker so subsequent logins are treated as
        // normal. The `authProvider` flip above also makes the forced
        // KindeMigrationModal auto-dismiss (eligibility requires `authProvider`
        // to still be KINDE).
        if (isMigration) {
          desktopAuthStore.clearMigrationInProgress();
        }

        // Drive the auth token cache through the same login transition.
        // Without this, authStateAtom stays at its pre-login value
        // (typically 'unauthenticated' or 'loading') and any consumer
        // gated on useIsAuthenticated() — including AuthWrapper —
        // doesn't see the user as authenticated until the next
        // window-focus event. clearAuthCache() drops any stale state;
        // the follow-up getAccessToken() reads the freshly-issued
        // session through the cache's Auth0-desktop branch and
        // transitions authStateAtom to 'authenticated'. Fire-and-forget
        // because the event handler is sync; the atom updates
        // propagate to consumers on the next render.
        void (async () => {
          try {
            const { clearAuthCache, getAccessToken } = await import('@/lib/auth');
            clearAuthCache();
            // getAccessToken never rejects — null means the refresh failed, and
            // that must not be published as a healthy completed login.
            const token = await getAccessToken();
            appendCheckpoint({
              key: TrackingKey.SCRIBE_LOGIN,
              checkpointName: 'auth_state_refreshed',
              metadata: { authRefreshed: token !== null },
            });
            // Finish on the frame after the authenticated state lands, whatever
            // screen the login flows into — anchoring the finish to one specific
            // landing surface would leak the entry for every cohort that lands
            // elsewhere (legacy list, onboarding, deep link).
            requestAnimationFrame(() => {
              finishTracking({ key: TrackingKey.SCRIBE_LOGIN });
            });
          } catch (error) {
            cancelTracking({ key: TrackingKey.SCRIBE_LOGIN });
            logger.error(
              '[DesktopAuthProvider] auth cache refresh after login failed',
              error
            );
          }
        })();
      } catch (error) {
        cancelTracking({ key: TrackingKey.SCRIBE_LOGIN });
        logger.error(
          '[DesktopAuthProvider] login success handler failed after taking perf ownership',
          error
        );
        throw error;
      }

      // Past this point the refresh chain owns the entry: a throw below must
      // NOT cancel it (the chain is about to finish it), so the tail sits
      // outside the try and rejects out of the handler as it always did.

      // Update local session state
      setAuthSession(newSession);

      // Track sign-in/sign-up success (Auth0-specific, uses loginIntent from desktopAuthSlice)
      trackAuthSuccess();

      // Clear audio file directory on login (aligned with KindeProvider's onRedirectCallback)
      void recorderFmc.clear_audio_file_directory();
    };

    const handleDownloadAttribution = (payload: DesktopDownloadAttributionPayload) => {
      // SCR-5478 Layer 3: opaque, untrusted attribution delivered on its own
      // event (never co-mingled with the credential response). The consumer
      // validates before use and never blocks login.
      attributeDownloadOnSignup(payload.downloadAttribution);
    };

    const initListeners = async () => {
      const unlisten = await setupAuthEventListeners({
        onLoginSuccess: handleLoginSuccess,
        onError: handleAuthError,
        onDownloadAttribution: handleDownloadAttribution,
      });

      if (cancelled) {
        unlisten();
      } else {
        cleanup = unlisten;
      }
    };

    void initListeners();

    return () => {
      cancelled = true;
      if (cleanup) {
        cleanup();
      }
    };
  }, []);

  const login = useCallback(async (options?: DesktopAuthLoginOptions) => {
    if (!desktopAuthBridge.isAvailable()) {
      logger.warn('[DesktopAuthProvider] login called outside Tauri runtime');
      return false;
    }

    // Capture prior provider so a login failure rolls back cleanly.
    // Without rollback, an aborted or user-denied login leaves
    // `authProvider === AUTH0_DESKTOP` persisted with no Auth0
    // session and the Kinde fallback in `useIsAuthenticated` is
    // `!isTauri()`-gated → user presents as signed-out until an
    // explicit logout. Two failure paths land here: the launch
    // itself throws (this function's catch block below), or the
    // deep-link `auth:error` event fires (handled in
    // `handleAuthError` above). The ref lets the async event
    // handler see this login is in flight and roll back safely.
    // Silent-abandonment (external browser closed without any
    // callback firing) is still not covered — no signal reaches
    // the FE.
    const priorProvider =
      store.getState().auth.authProvider ?? getPersistedAuthProvider() ?? null;
    loginAttemptRef.current = { priorProvider };

    // Set authProvider early to prevent race conditions in TauriWrapper.
    // This ensures isAuth0Desktop is true before any login-related effects run,
    // preventing clearPersistedModels() from being called incorrectly during
    // the login flow.
    setAuthProviderAsSessionCookie(AuthProvider.AUTH0_DESKTOP);

    try {
      await launchDesktopAuthLogin(options);
      return true;
    } catch (error) {
      loginAttemptRef.current = null;
      cancelTracking({ key: TrackingKey.SCRIBE_LOGIN });
      if (priorProvider !== null) {
        store.dispatch.auth._setAuthProvider(priorProvider);
      } else {
        store.dispatch.auth.clearAuthProvider();
      }
      logger.warn(
        '[DesktopAuthProvider] launchDesktopAuthLogin failed; provider restored',
        {
          error: error instanceof Error ? error.message : String(error),
          priorProvider,
        }
      );
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    if (!desktopAuthBridge.isAvailable()) {
      logger.warn('[DesktopAuthProvider] logout called outside Tauri runtime');
      return;
    }

    // Delegate to authModel.logout() which handles all cleanup and navigation
    // This ensures consistent logout behavior regardless of how logout is triggered
    await logout();
  }, []);

  // Derive isAuthenticated and isLoading from authSession
  const isAuthenticated = authSession?.isAuthenticated ?? false;
  const isLoading = authSession === null;

  const value = useMemo<DesktopAuthContextValue>(
    () => ({
      session: authSession,
      isAuthenticated,
      isLoading,
      login,
      logout: signOut,
    }),
    [authSession, isAuthenticated, isLoading, login, signOut]
  );

  return (
    <DesktopAuthContext.Provider value={value}>{children}</DesktopAuthContext.Provider>
  );
}
